import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client.js';
import { getJnHoldBlock, setJnHoldBlock } from '../jn-hold-block-store.js';
import type { AuthHoldCheckResult } from '../screens/AuthHoldPopup.js';
import type { JnHoldResult } from './jn-hold.js';

/**
 * The hold result paired with the CIF it was resolved for, so the pop-up is only
 * ever rendered for the CURRENT customer. Keying by customer prevents customer
 * A's hold from surfacing for customer B if the identity changes before a fresh
 * check resolves (or fails open).
 */
type KeyedAuthHold = {
  customerNumber: string;
  result: AuthHoldCheckResult;
};

/**
 * The J&N-hold verdict (issue #669) paired with the CIF it was resolved for —
 * same cross-customer safety as {@link KeyedAuthHold}. The J&N verdict rides on
 * the SAME `/idv/auth-hold-check` response (an additive `jnHold` field), so a
 * registering customer with a simultaneous J+N hold raises the warning from this
 * single ID&V call — no separate (medium-gated) accounts read. A found verdict
 * is ALSO published to the app-shell navigation-lock store
 * (`jn-hold-block-store.ts`), which renders the NON-DISMISSIBLE `JnHoldPopup`
 * in place of the routed screens on any route (@dylan219, 2026-09-01 — the
 * pop-up has no acknowledge button and the agent cannot navigate off it).
 */
type KeyedJnHold = {
  customerNumber: string;
  result: JnHoldResult;
};

/**
 * Type guard: does the response's optional `jnHold` field carry a renderable
 * J&N pop-up payload? A well-formed payload has a non-empty `accounts` array
 * (each with a string `accountLast4` + a `holdCodes` string array) and a string
 * `scripting`. Anything short of this is treated as "no J&N pop-up" — the check
 * simply does not surface the pop-up (the authoritative stop is the server-side
 * `requireNoJnHoldBlock` guard, which this route already recorded).
 */
function isRenderableJnHold(data: unknown): data is JnHoldResult {
  if (data === null || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.scripting !== 'string') return false;
  if (!Array.isArray(d.accounts) || d.accounts.length === 0) return false;
  return d.accounts.every((a) => {
    if (a === null || typeof a !== 'object') return false;
    const acc = a as Record<string, unknown>;
    return (
      typeof acc.accountLast4 === 'string' &&
      Array.isArray(acc.holdCodes) &&
      acc.holdCodes.every((c) => typeof c === 'string')
    );
  });
}

/**
 * Type guard: does the fetched payload carry every field `AuthHoldPopup` reads
 * when a hold is present? A well-formed hold must have boolean flags, an
 * `accounts` array (each entry with the per-account display fields the pop-up
 * renders) and a `customer` group with `names`/`accountTypes` arrays. Anything
 * short of this is treated as malformed — the check fails open (nothing shown)
 * rather than rendering a pop-up that would crash on a missing field.
 */
function isRenderableAuthHold(data: unknown): data is AuthHoldCheckResult {
  if (data === null || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (typeof d.blocked !== 'boolean' || typeof d.hasHold !== 'boolean') {
    return false;
  }
  if (!Array.isArray(d.accounts)) return false;
  const accountsOk = d.accounts.every((a) => {
    if (a === null || typeof a !== 'object') return false;
    const acc = a as Record<string, unknown>;
    return (
      typeof acc.index === 'number' &&
      typeof acc.accountLast4 === 'string' &&
      Array.isArray(acc.holdCodes) &&
      typeof acc.scripting === 'string'
    );
  });
  if (!accountsOk) return false;
  if (d.customer === null || typeof d.customer !== 'object') return false;
  const c = d.customer as Record<string, unknown>;
  return Array.isArray(c.names) && Array.isArray(c.accountTypes);
}

/**
 * CCF-43 — the shared Customer Authentication Hold Check hook.
 *
 * This is the SINGLE client-side implementation of the CCF-43 hold check + its
 * pop-up-raising decision. It calls the existing, Teller-ID + `checkAuthHold`
 * gated `POST /idv/auth-hold-check` BFF route (which derives the customer from
 * the server-side ID&V session and returns the 4-item pop-up payload) and
 * FAILS OPEN exactly as the story mandates: on any non-success response, error
 * body, or malformed result the agent simply continues — nothing is surfaced.
 * Only a well-formed result that carries an actual hold raises the pop-up.
 *
 * CCF-43 states "Below should apply to all hold checks" — so this hook is used
 * BOTH on the Customer Dashboard (the Online-login landing, issue #67) AND when
 * the agent lands on a Registration security screen (issue #666): a customer who
 * is registering must have the same auth hold check completed + pop-up raised.
 * Reusing one hook keeps the gateway call, the fail-open contract, and the
 * `AuthHoldPopup` behaviour identical on every screen.
 *
 * The check runs once a customer CIF + a 4-digit Teller ID are in context. The
 * caller may additionally gate it (e.g. the Dashboard SKIPS it for the explicit
 * offline sort-code + account-number journey, per issue #67) via `enabled`.
 *
 * The pop-up is AUTO-opened only when the agent has not already acknowledged it
 * for this customer on this call (issue #574). The acknowledgement flag is read
 * through a ref so it never re-runs (and re-fetches) the effect — the exact
 * stale-closure / effect-race pattern (#201/#243/#244/#422) this codebase has
 * been repeatedly bitten by.
 *
 * The SAME `/idv/auth-hold-check` response ALSO carries the #669 J & N Hold
 * Handling verdict (issue #666): a simultaneous `J`+`N` hold on the identified
 * customer's accounts is a fraud/AML stop, distinct from the CCF-43 auth holds
 * (J/N are not auth-hold codes). This hook surfaces it as a separate verdict
 * (`jnHold`), keyed to the current customer the same way, and PUBLISHES a found
 * verdict to the app-shell navigation-lock store (`setJnHoldBlock`), so the
 * registration security screens (SCRN-17..21) raise the warning from this single
 * ID&V call. Unlike the CCF-43 pop-up, the J&N warning is NON-DISMISSIBLE and
 * locks navigation app-shell-wide (@dylan219, 2026-09-01) — there is no
 * open/close/acknowledge state for it in this hook any more. The authoritative
 * stop is the server-side `requireNoJnHoldBlock` guard, which the same route
 * records; the store only drives the client warning + navigation lock.
 *
 * IMPORTANT — the J&N stop does NOT inherit the CCF-43 fail-open. The CCF-43
 * auth-hold pop-up may fail open (an unreachable/malformed check just continues,
 * per issue #67) because a missed auth hold does not silently *record* a stop.
 * The J&N verdict is a fraud/AML HARD STOP whose authoritative enforcement is
 * the server-side `requireNoJnHoldBlock` block RECORDED BY THIS SAME
 * `/idv/auth-hold-check` call — so if this call cannot be completed
 * (error/non-OK/thrown transport, or a payload we cannot type-guard) we CANNOT
 * assume "no J&N block": the block may not have been recorded server-side and
 * the agent would continue with neither a pop-up nor a stop (the ai-review HIGH
 * on #666). We therefore model the read OUTCOME and, on a failed read, return an
 * explicit `jnHoldError` (indeterminate) state — the permissive "no J&N stop"
 * verdict is returned ONLY on a genuinely-completed read. Callers on the
 * registration security screens must fail CLOSED on `jnHoldError`: disable
 * progression / transfer and surface an error until the check can be re-run.
 */
export function useAuthHoldCheck({
  customerNumber,
  tellerId,
  enabled = true,
  online = true,
  acknowledged,
  onAcknowledge,
}: {
  /** The identified customer's CIF (from ID&V). */
  customerNumber: string;
  /** The mandatory 4-digit Teller ID. */
  tellerId: string;
  /**
   * The server-pinned Online/Offline journey (`data.state` from `GET /session`).
   * @dylan219 #784 made the CCF-43 auth-hold + J&N fraud/AML check ONLINE-ONLY:
   * it protects a live Genesys interaction, so on an OFFLINE journey there is no
   * live call to protect and the check must be SKIPPED entirely.
   *   - `true` (the DEFAULT, so every existing call-site/test that omits this is
   *     unchanged) — run the check exactly as before.
   *   - `false` (OFFLINE) — do NOT run the check; resolve the J&N tri-state to
   *     determinate-complete-with-NO-block (see the effect) so offline
   *     registration can progress. This "no J&N block offline" is @dylan219's
   *     AUTHORISED #784 behaviour, NOT a fail-open regression of the #666/#669
   *     fail-closed (which still fully applies ONLINE).
   *   - `null` (INDETERMINATE — the caller passed null while `GET /session` is
   *     still resolving) — do NOT run and do NOT mark complete, so registration
   *     stays fail-CLOSED until the online/offline state is known.
   */
  online?: boolean | null;
  /**
   * When false the check is skipped entirely (no fetch, no pop-up). Defaults to
   * true. The Dashboard passes `idvJourney !== 'nsc-account'` here so the check
   * is skipped for the offline journey (issue #67); registration always enables
   * it (a registering customer is always the online-card journey).
   */
  enabled?: boolean;
  /**
   * Whether the agent has already acknowledged the pop-up for this customer on
   * this call (issue #574). When true the pop-up is not auto-raised, but a
   * returned hold is still recorded so an on-demand re-open keeps working.
   */
  acknowledged?: boolean;
  /** Persist the acknowledgement (issue #574) when the agent closes the pop-up. */
  onAcknowledge?: () => void;
  /**
   * Whether the agent has already acknowledged the J&N-hold pop-up (issue #669)
   * for this customer on this call. Same semantics as `acknowledged` but for the
   * distinct J&N fraud/AML stop.
   */
  jnAcknowledged?: boolean;
  /** Persist the J&N-hold acknowledgement when the agent closes that pop-up. */
  onJnAcknowledge?: () => void;
}): {
  /** The returned hold result (a hold was found), or null. */
  authHold: AuthHoldCheckResult | null;
  /** Whether the pop-up is currently open. */
  authHoldOpen: boolean;
  /** Close the pop-up and record the acknowledgement. */
  closeAuthHold: () => void;
  /** Re-open the pop-up on demand (e.g. a "Review hold details" button). */
  openAuthHold: () => void;
  /**
   * The J&N-hold verdict (issue #669) for the CURRENT customer, or null. Rides
   * on the SAME `/idv/auth-hold-check` response — a simultaneous J+N hold on the
   * identified customer's accounts (distinct from the CCF-43 auth holds above).
   * A found verdict is ALSO published to the app-shell navigation-lock store
   * (see the function docs): the warning itself is rendered app-shell-wide by
   * App.tsx, non-dismissible, with no open/close/acknowledge state here.
   */
  jnHold: JnHoldResult | null;
  /**
   * Indeterminate (fail-CLOSED) state for the J&N fraud/AML stop: the
   * `/idv/auth-hold-check` read could NOT be completed for the current customer
   * (error/non-OK/thrown transport, or a payload that failed the type guard), so
   * we could not confirm the ABSENCE of a J&N block — and, crucially, the
   * server-side `requireNoJnHoldBlock` block may not have been recorded either.
   * Distinct from `jnHold === null` (a completed read that found no J&N stop).
   * Registration security screens MUST fail closed on this (disable
   * progression/transfer + surface an error) rather than continuing. The CCF-43
   * auth-hold pop-up above keeps its story-mandated fail-open and does NOT set
   * this. `null` while the check is disabled / has no context / has not resolved.
   */
  jnHoldError: boolean;
  /**
   * Whether the J&N fraud/AML check has COMPLETED with a determinate verdict for
   * the current customer — i.e. the `/idv/auth-hold-check` read resolved 2xx and
   * either surfaced a J&N stop or confirmed its ABSENCE (and the server-side
   * `requireNoJnHoldBlock` block was recorded on that same successful call).
   *
   * This is the tri-state that closes the ai-review HIGH (#666): `jnHoldError`
   * alone is INSUFFICIENT to fail closed, because while the async request is
   * still in flight (or has not started) `jnHoldError` is `false` — an agent
   * could satisfy the questions and progress/transfer BEFORE the check confirms
   * the absence of a J&N hold. Callers must therefore require `jnHoldCheckComplete`
   * to be `true` (and `jnHoldError` to be `false`) before allowing progression —
   * treating the idle/loading/pending window as BLOCKED (fail closed). It is
   * `false` while the check is disabled / has no context / is still loading, and
   * is reset to `false` at the start of every effective check so it can never
   * linger across a customer/context change or survive a re-run.
   */
  jnHoldCheckComplete: boolean;
} {
  // The stored result is keyed by the CIF it was resolved for so a stale hold
  // can never render for a different customer (see `KeyedAuthHold`).
  const [keyedHold, setKeyedHold] = useState<KeyedAuthHold | null>(null);
  const [authHoldOpen, setAuthHoldOpen] = useState(false);
  // The J&N verdict is keyed the same way (see `KeyedJnHold`). It has NO
  // open/close state: a found verdict is published to the app-shell
  // navigation-lock store, which renders the non-dismissible warning on every
  // route (#669, @dylan219 2026-09-01).
  const [keyedJnHold, setKeyedJnHold] = useState<KeyedJnHold | null>(null);
  // The J&N read OUTCOME, keyed to the customer it resolved for. `true` = the
  // read could not be completed (fail CLOSED — see `jnHoldError` in the return
  // type); reset to `null` at the top of every effective check so it can never
  // linger across a customer/context change. Keyed so a late error for a
  // superseded customer can never surface for the current one.
  const [keyedJnError, setKeyedJnError] = useState<{
    customerNumber: string;
  } | null>(null);
  // The J&N read has COMPLETED with a determinate verdict, keyed to the customer
  // it resolved for. Set ONLY on a genuinely-completed 2xx read (a J&N stop found
  // OR its absence confirmed — the server-side `requireNoJnHoldBlock` block was
  // recorded on that same call). Reset to `null` at the top of every effective
  // check so the idle/loading window reads as NOT complete (fail CLOSED — see
  // `jnHoldCheckComplete` in the return type). Keyed so a late completion for a
  // superseded customer can never mark the current one complete.
  const [keyedJnComplete, setKeyedJnComplete] = useState<{
    customerNumber: string;
  } | null>(null);

  // Read the acknowledgement through a ref inside the async check rather than
  // adding it to the effect's dependency array — adding it would re-run the
  // effect (and re-fetch). The ref is kept in sync by its own effect.
  const ackRef = useRef(acknowledged);
  useEffect(() => {
    ackRef.current = acknowledged;
  }, [acknowledged]);

  useEffect(() => {
    // Start every effective check (new customer/teller key, or a disabled /
    // no-context state) from a clean slate: clear any hold carried over from a
    // PRIOR customer/check so it can never persist across customers or survive a
    // failed/empty check. Only a fresh, well-formed hold for the CURRENT
    // customer re-populates the state below.
    setKeyedHold(null);
    setAuthHoldOpen(false);
    setKeyedJnHold(null);
    // #669 navigation lock (2026-09-01): start a fresh check WITHOUT a stale
    // app-shell warning for this customer — a re-run that comes back CLEAR must
    // not leave the agent locked behind the previous verdict. Keyed to the
    // customer so a block belonging to a DIFFERENT customer is never touched.
    if (customerNumber && getJnHoldBlock()?.customerNumber === customerNumber) {
      setJnHoldBlock(null);
    }
    // Clear any prior J&N read-outcome error: a disabled / no-context state is
    // not an error (nothing to fail closed on), and a fresh check must start
    // clean so a stale error can never leak across a customer/context change.
    setKeyedJnError(null);
    // Clear the prior "check complete" verdict: a re-run (new customer/teller
    // key) starts in the LOADING state — not yet complete — so callers fail
    // closed on the in-flight window (the ai-review HIGH). Only a
    // genuinely-completed 2xx read below re-marks it complete for THIS customer.
    setKeyedJnComplete(null);

    if (!enabled) return;

    // @dylan219 #784 — the CCF-43 auth-hold + J&N fraud/AML check is ONLINE-ONLY.
    // `online` gates it AFTER the top-of-effect state resets (so a prior verdict
    // is always cleared) and after `enabled`, but BEFORE the customer/teller
    // context check.
    if (online === null || online === undefined) {
      // INDETERMINATE — the caller passed null while `GET /session` is still
      // resolving. Do NOT run and do NOT mark complete: the reset above already
      // cleared `keyedJnComplete`, so `jnHoldCheckComplete` stays false and
      // registration remains fail-CLOSED until the online/offline state is known.
      return;
    }
    if (online === false) {
      // OFFLINE — do NOT run the check. This is the @dylan219 #784 online-only
      // rule: the fraud/AML J&N stop (and the CCF-43 auth-hold pop-up) protect a
      // LIVE Genesys interaction, so with no live call there is nothing to
      // protect and the check does not apply offline. Resolve the J&N tri-state
      // to determinate-complete-with-NO-block so an OFFLINE registration can
      // progress: mark the check complete (for the current customer) and leave
      // keyedJnHold/keyedJnError/keyedHold null (all cleared by the reset above).
      //
      // NB: this offline "no J&N block" is @dylan219's AUTHORISED #784 behaviour,
      // NOT a fail-open regression of the #666/#669 fail-closed — the fail-closed
      // (an indeterminate/failed read blocks progression) still FULLY applies
      // ONLINE, where a live call must be protected. Offline there is no call, so
      // no block is the correct, authorised verdict.
      if (customerNumber) {
        setKeyedJnComplete({ customerNumber });
      }
      return;
    }

    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;

    // Capture the customer this check is FOR so the resolved result is keyed to
    // it — a late-resolving response for a superseded customer is discarded by
    // `cancelled`, and the stored key is asserted at render time.
    const forCustomer = customerNumber;
    let cancelled = false;
    async function runAuthHoldCheck(): Promise<void> {
      try {
        // The BFF derives the customer (CIF + name) from the server-side ID&V
        // session; the customerNumber is sent only as a defence-in-depth
        // cross-check (the server rejects a mismatch).
        const { data, error, response } = await api.POST(
          '/idv/auth-hold-check',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber: forCustomer },
          },
        );
        if (cancelled) return;
        // The J&N fraud/AML stop fails CLOSED, distinct from the auth-hold
        // fail-open. A transport error or a non-OK response means the call did
        // not complete — the server-side `requireNoJnHoldBlock` block may not
        // have been recorded and we cannot confirm the ABSENCE of a J&N stop —
        // so flag `jnHoldError` (keyed to this customer) BEFORE the fail-open
        // return below. A completed-but-2xx response (error === undefined &&
        // response.ok) is treated as determinate for J&N even if the AUTH-hold
        // payload is malformed: the route records the J&N block from the same
        // (successful) accounts read regardless of the auth-hold shape.
        if (error || !response.ok) {
          setKeyedJnError({ customerNumber: forCustomer });
          return;
        }
        // FAIL OPEN (auth hold only): on a malformed result the agent simply
        // continues — nothing is surfaced. The type guard rejects a hold that is
        // missing any field `AuthHoldPopup` needs (e.g. a non-array `accounts`),
        // so the pop-up never renders on a partial payload. The state was reset
        // at the top of the effect, so returning here leaves no stale hold. The
        // J&N branch below still runs on a well-formed 2xx envelope even when the
        // auth-hold portion is malformed.
        if (!isRenderableAuthHold(data)) {
          // The overall response is 2xx and well-formed enough to parse the J&N
          // portion; evaluate it before returning so a malformed AUTH-hold shape
          // never suppresses a genuine J&N stop.
          if (isRenderableJnHold((data as { jnHold?: unknown }).jnHold)) {
            setKeyedJnHold({
              customerNumber: forCustomer,
              result: (data as { jnHold: JnHoldResult }).jnHold,
            });
            // Publish to the app-shell navigation-lock store: the warning is
            // rendered NON-DISMISSIBLY in place of the routed screens on every
            // route (#669, @dylan219 2026-09-01).
            setJnHoldBlock({
              customerNumber: forCustomer,
              result: (data as { jnHold: JnHoldResult }).jnHold,
            });
          }
          // The J&N verdict is DETERMINATE on this successful 2xx read (the route
          // recorded any `requireNoJnHoldBlock` block from the same accounts
          // read), even though the AUTH-hold portion was malformed — mark the
          // check complete so callers may progress.
          setKeyedJnComplete({ customerNumber: forCustomer });
          return;
        }
        if (data.hasHold) {
          // SET the hold (keyed to this customer) so an on-demand re-open keeps
          // working. Only AUTO-open the pop-up when the agent has not already
          // acknowledged it for this customer on this call (issue #574).
          setKeyedHold({ customerNumber: forCustomer, result: data });
          if (!ackRef.current) setAuthHoldOpen(true);
        }
        // Issue #669 / #666 — the J&N-hold verdict rides on the SAME response
        // (an additive optional `jnHold` field). It is INDEPENDENT of the CCF-43
        // auth hold above: a simultaneous J+N hold can be present even when
        // `hasHold` is false (J/N are not auth-hold codes), so it is evaluated
        // separately. The type guard rejects a missing/partial payload — then no
        // J&N pop-up is surfaced (the authoritative stop is the server-side
        // `requireNoJnHoldBlock` guard this route already recorded).
        if (isRenderableJnHold((data as { jnHold?: unknown }).jnHold)) {
          setKeyedJnHold({
            customerNumber: forCustomer,
            result: (data as { jnHold: JnHoldResult }).jnHold,
          });
          // Publish to the app-shell navigation-lock store: the warning is
          // rendered NON-DISMISSIBLY in place of the routed screens on every
          // route (#669, @dylan219 2026-09-01).
          setJnHoldBlock({
            customerNumber: forCustomer,
            result: (data as { jnHold: JnHoldResult }).jnHold,
          });
        }
        // The J&N read has COMPLETED with a determinate verdict on this
        // successful 2xx response (a J&N stop was surfaced above OR its absence
        // was confirmed — either way the server-side `requireNoJnHoldBlock` block
        // was recorded on this same call). Mark the check complete so callers may
        // progress; until this point `jnHoldCheckComplete` stays false and the
        // registration screens fail closed on the in-flight window.
        setKeyedJnComplete({ customerNumber: forCustomer });
      } catch {
        // The AUTH-hold pop-up FAILS OPEN on a network/transport failure (issue
        // #67) — nothing is surfaced. But the J&N fraud/AML stop FAILS CLOSED: a
        // thrown transport failure means the `/idv/auth-hold-check` call did not
        // complete, so the server-side `requireNoJnHoldBlock` block was NOT
        // recorded and we cannot confirm the absence of a J&N stop. Flag the
        // indeterminate J&N state (keyed to this customer) so the registration
        // screens fail closed. Guard on `cancelled` so a late throw for a
        // superseded customer never surfaces.
        if (!cancelled) setKeyedJnError({ customerNumber: forCustomer });
      }
    }

    void runAuthHoldCheck();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, enabled, online]);

  // Only surface the stored hold when it belongs to the CURRENT customer — a
  // last line of defence against customer A's hold showing for customer B.
  const authHold =
    keyedHold && keyedHold.customerNumber === customerNumber
      ? keyedHold.result
      : null;
  // Same current-customer assertion for the J&N verdict.
  const jnHold =
    keyedJnHold && keyedJnHold.customerNumber === customerNumber
      ? keyedJnHold.result
      : null;
  // Same current-customer assertion for the J&N read-outcome error, so a stale
  // error from a superseded customer can never fail the current one closed.
  const jnHoldError =
    keyedJnError !== null && keyedJnError.customerNumber === customerNumber;
  // Same current-customer assertion for the "check complete" verdict, so a late
  // completion for a superseded customer can never mark the current one complete
  // (the fail-closed in-flight window must persist across a customer swap).
  const jnHoldCheckComplete =
    keyedJnComplete !== null &&
    keyedJnComplete.customerNumber === customerNumber;

  return {
    authHold,
    authHoldOpen: authHold ? authHoldOpen : false,
    openAuthHold: () => setAuthHoldOpen(true),
    closeAuthHold: () => {
      setAuthHoldOpen(false);
      onAcknowledge?.();
    },
    jnHold,
    jnHoldError,
    jnHoldCheckComplete,
  };
}
